import { Divider, Headline, Text, TextLink } from '@oneaudi/unified-web-alpha-components';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import { RegisterOrUpdateConsumptionFootnoteForDialog } from '~/app/compositions/content/calculation-info/components/consumption-footnote';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { DialogProps } from '~/app/pages/summary/dialogs/dialog-props';
import {
	RateSectionContainerStyles,
	StyledPriceDetailContainer,
} from '~/app/pages/summary/dialogs/price-detail/core/styles/price-details-style';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction, LayerType } from '~/app/tracking/feld-m-types';
import { Dialog } from '~/components/dialog';
import { useIframeDialog } from '~/components/dialog/iframe-dialog/iframe-dialog';
import { useFootnotes } from '~/components/footnotes';
import { CAE_CONFIGUREDCAR_REMARK } from '~/components/footnotes/constants';
import { FootnoteReferenceNgc } from '~/components/footnotes/footnote-reference';
import { RenderHtml } from '~/components/text';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCarlineQuery } from '~/graphql/hooks/use-configured-car-queries';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { state } from '~/storage';
import { PriceSection } from '../components/price-section';
import { RateSection } from '../components/rate-section';

export function PriceDetailsLegalNetherlandsDialog({ open, onClose }: DialogProps) {
	useEffect(() => {
		if (open) {
			appTrackingService.updateLayerComponent(LayerType.PriceDetailsLayer, 'open');
			appTrackingService.viewChangeEvent({
				eventName: `configurator - ${LayerType.PriceDetailsLayer}`,
				componentName: 'price-details-layer',
			});
		}
	}, [open]);
	return (
		<Dialog open={open} onCloseButtonClick={onClose}>
			<PriceDetailsDialogContent />
		</Dialog>
	);
}

function PriceDetailsDialogContent() {
	const { catalog, carIdentifier } = useConfigurator();
	const { config } = useMarketConfig();
	const { i18n, i18nKeys } = useI18n();
	const { formatCurrency, formatMonthlyRate } = useCurrencyFormatter();
	const vatConfig = useVATConfig();
	const { priceSummary } = useCatalog();
	const { openDialog, closeDialog } = useDialogs();
	const financialProducts = useAtomValue(state.financialProducts);
	const selectedProductSettings = useAtomValue(state.fsagFinancialCalculatorSettings);
	const { priceDateRemark } = usePriceDetailRemarks(config.useStaticPriceDateRemark);
	const { getReference } = useFootnotes();
	let product: string;
	const defaultProduct = useAtomValue(state.defaultFinancialProduct);
	const { showIframeDialog } = useIframeDialog();
	const carlineData = useCarlineQuery(carIdentifier, config.financingInput);

	if (config.showCaESummaryText) {
		RegisterOrUpdateConsumptionFootnoteForDialog();
	}

	// price order according to the market, total price is rotr.
	const priceOrder = ['model', 'options', 'vat', 'divider', 'total', 'rotrRate', 'divider', 'rotr'];

	const prices = priceSummary?.parts || [];

	if (selectedProductSettings) {
		product = selectedProductSettings['@ID'] ?? '';
	} else {
		product = defaultProduct ?? '';
	}

	const carType =
		carlineData?.carline?.configuredCar?.carlineStructureCarline?.vehicleType ||
		carlineData?.carline?.configuredCar?.technicalDataV2?.modelAttributes?.driveTrain;
	const priceDisclaimerText = useMemo(() => {
		if (config.enablePriceDisclaimer) {
			return carType
				? carType === 'BEV'
					? i18n('ngc.remarks.price.legalBEV')
					: i18n('ngc.remarks.price.legalICE')
				: undefined;
		}
		return null;
	}, [config.enablePriceDisclaimer, carType, i18n]);

	return (
		<StyledPriceDetailContainer>
			{}
			<div tabIndex={0} className="header-section">
				<Headline tag="h2" variant={{ type: 'headline', order: '1', weight: 'normal' }}>
					{catalog.carInfo?.carline}
				</Headline>
				<Headline tag="h3" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
					{catalog.carInfo?.model}
					&nbsp;
					<FootnoteReferenceNgc
						reference={getReference(CAE_CONFIGUREDCAR_REMARK)}
						ariaLabel={i18n('ngc.a11y.footnotes.cae')}
					/>
				</Headline>
			</div>

			{}
			<div tabIndex={0} className="title-section">
				<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>{i18n('ngc.priceDetails.title')}</Text>
			</div>

			{/* Price detail including total price */}
			<ul className="price-list">
				{priceOrder.map((price) => {
					return price === 'divider' ? (
						<Divider key={price} />
					) : (
						<PriceSection
							key={price}
							pricePartType={price}
							title={i18n(`ngc.priceDetails.${price}` as keyof typeof i18nKeys)}
							price={formatCurrency(
								prices.find((part) => part.type === price)?.price,
								vatConfig.priceParts[price],
							)}
							disclaimer={price === 'rotr' ? priceDisclaimerText : null}
							bold={price === 'rotr'}
						/>
					);
				})}
			</ul>

			{/* Leasing price */}
			{catalog?.priceSummary?.monthlyRate && config.enableFinancingServices && (
				<RateSectionContainerStyles>
					<RateSection
						pricePartType="totalRate"
						title={
							financialProducts.find((prod) => {
								return prod.key === product;
							})?.val ?? i18n('ngc.priceDetails.privateLeasing')
						}
						price={formatMonthlyRate(catalog.priceSummary?.monthlyRate)}
						bold
					/>
					{}

					<TextLink
						onClick={() => {
							closeDialog('priceDetails');
							appTrackingService.summaryClickEvent({
								eventAction: EventAction.InternalLink,
								eventName: `configurator (price details layer) - click to open financing options layer`,
								componentName: 'price-details-layer',
								label: i18n('ngc.actions.changeLeasingRate'),
							});
							openDialog('monthlyPriceRatesFsag');
						}}
						size="sm"
						iconPosition={'trailing'}
						icon={'forward'}
						variant="standalone">
						{i18n('ngc.actions.changeLeasingRate')}
					</TextLink>
					{config.deliveryCostUrl && (
						<TextLink
							onClick={
								config.useIframe
									? () =>
											showIframeDialog({
												url: config.deliveryCostUrl,
												name: LayerType.DeliveryCostLayer,
											})
									: () => window.open(config.specialOfferUrl)
							}
							size="sm"
							iconPosition={'trailing'}
							icon={'forward'}
							variant="standalone">
							{i18n('ngc.priceDetails.deliveryCosts')}
						</TextLink>
					)}
				</RateSectionContainerStyles>
			)}
			{priceDateRemark && (
				<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>
					{}
					<RenderHtml tabIndex={0} html={priceDateRemark} />
				</Text>
			)}
		</StyledPriceDetailContainer>
	);
}

/**
 * Returns the remark text to be shown in the bottom of the price details layer.
 */
function usePriceDetailRemarks(staticRemark: boolean): {
	priceDateRemark: string | undefined | null;
} {
	const { i18n } = useI18n();
	const { priceSummary } = useCatalog();
	return {
		priceDateRemark: staticRemark ? i18n('ngc.summary.remark') : priceSummary?.priceDate?.text,
	};
}
